import React, { useRef, useState, useEffect } from "react";
import { Modal } from "../../components/tailwind/modal/Modal";
import { Button } from "../../components/tailwind/button/Button";
import {
  useSyncFromInriverMutation,
  SyncFromInriverResponse,
  SyncType,
} from "../../api/customerFieldApi";
import { SyncOptionsStep, OverwriteExistingCheckbox } from "./SyncOptionsStep";

type SyncState = "idle" | "syncing" | "success" | "error";
type ModalStep = "choose" | "fieldset-options" | "specification-options";

type Props = {
  onClose: () => void;
  syncFieldsetCategoryFilter?: string[];
  canSyncSpecifications?: boolean;
};

export const InriverSyncModal: React.FC<Props> = ({
  onClose,
  syncFieldsetCategoryFilter,
  canSyncSpecifications,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const isMountedRef = useRef(true);
  const [syncState, setSyncState] = useState<SyncState>("idle");
  const [step, setStep] = useState<ModalStep>("choose");
  const [result, setResult] = useState<SyncFromInriverResponse | null>(null);
  const [syncFromInriver] = useSyncFromInriverMutation();
  const [overwriteExisting, setOverwriteExisting] = useState(false);

  useEffect(() => {
    dialogRef.current?.showModal();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleClose = (): void => {
    dialogRef.current?.close();
    onClose();
  };

  const handleSync = async (
    syncType: SyncType,
    options?: { overwrite_existing?: boolean }
  ): Promise<void> => {
    if (!isMountedRef.current) return;
    setSyncState("syncing");
    setResult(null);

    try {
      const response = await syncFromInriver({
        sync_type: syncType,
        ...options,
      }).unwrap();
      if (!isMountedRef.current) return;
      setResult(response);
      setSyncState(response.success ? "success" : "error");
    } catch (err) {
      if (!isMountedRef.current) return;
      setResult({
        success: false,
        message: "Something went wrong",
      });
      setSyncState("error");
    }
  };

  const handleSyncAgain = (): void => {
    setSyncState("idle");
    setStep("choose");
    setResult(null);
    setOverwriteExisting(false);
  };

  const handleFieldsetsClick = (): void => {
    setStep("fieldset-options");
  };

  const handleSpecificationsClick = (): void => {
    setStep("specification-options");
  };

  const handleBack = (): void => {
    setStep("choose");
  };

  return (
    <Modal dialogRef={dialogRef} size="sm">
      <Modal.Header divide>
        <h5
          className="tw-text-lg tw-font-semibold"
          data-testid="inriver-sync-modal-header"
        >
          Import from InRiver
        </h5>
      </Modal.Header>
      <Modal.Body className="tw-py-6">
        {syncState === "idle" && step === "choose" && (
          <div className="tw-space-y-4">
            <p className="tw-text-gray-600 tw-mb-6">
              Import field definitions or input schemas from your InRiver PIM
              system.
            </p>

            <div className="tw-space-y-3">
              <button
                type="button"
                data-testid="import-pim-fields-btn"
                onClick={() => handleSync("fields")}
                className="tw-w-full tw-text-left tw-p-4 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-border-blue-500 hover:tw-bg-blue-50 tw-transition-colors"
              >
                <div className="tw-font-medium tw-text-gray-900">
                  Import PIM Fields
                </div>
                <div className="tw-text-sm tw-text-gray-500 tw-mt-1">
                  Syncs field definitions from your InRiver entity types.
                  Creates or updates PIM Fields in Textual.
                </div>
              </button>

              <button
                type="button"
                data-testid="import-fieldsets-btn"
                onClick={handleFieldsetsClick}
                className="tw-w-full tw-text-left tw-p-4 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-border-blue-500 hover:tw-bg-blue-50 tw-transition-colors"
              >
                <div className="tw-font-medium tw-text-gray-900">
                  Import Input Schemas
                </div>
                <div className="tw-text-sm tw-text-gray-500 tw-mt-1">
                  Syncs fieldsets from InRiver. Creates Input Schemas in Textual
                  that group fields together.
                </div>
              </button>

              {canSyncSpecifications && (
                <>
                  <button
                    type="button"
                    data-testid="import-spec-fields-btn"
                    onClick={() => handleSync("specification_fields")}
                    className="tw-w-full tw-text-left tw-p-4 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-border-blue-500 hover:tw-bg-blue-50 tw-transition-colors"
                  >
                    <div className="tw-font-medium tw-text-gray-900">
                      Import Specification Fields
                    </div>
                    <div className="tw-text-sm tw-text-gray-500 tw-mt-1">
                      Syncs field definitions from SpecificationTemplates.
                    </div>
                  </button>

                  <button
                    type="button"
                    data-testid="import-specifications-btn"
                    onClick={handleSpecificationsClick}
                    className="tw-w-full tw-text-left tw-p-4 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-border-blue-500 hover:tw-bg-blue-50 tw-transition-colors"
                  >
                    <div className="tw-font-medium tw-text-gray-900">
                      Import Specification Templates
                    </div>
                    <div className="tw-text-sm tw-text-gray-500 tw-mt-1">
                      Syncs SpecificationTemplates from InRiver as Input
                      Schemas.
                    </div>
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {syncState === "idle" && step === "fieldset-options" && (
          <SyncOptionsStep
            onBack={handleBack}
            onSync={() =>
              handleSync("fieldsets", {
                overwrite_existing: overwriteExisting,
              })
            }
            syncButtonTestId="sync-fieldsets-btn"
          >
            <OverwriteExistingCheckbox
              checked={overwriteExisting}
              onChange={setOverwriteExisting}
            />

            {syncFieldsetCategoryFilter &&
              syncFieldsetCategoryFilter.length > 0 && (
                <p className="tw-text-sm tw-text-gray-500">
                  Only fields from these categories will be synced:{" "}
                  {syncFieldsetCategoryFilter.join(", ")}
                </p>
              )}
          </SyncOptionsStep>
        )}

        {syncState === "idle" && step === "specification-options" && (
          <SyncOptionsStep
            onBack={handleBack}
            onSync={() =>
              handleSync("specifications", {
                overwrite_existing: overwriteExisting,
              })
            }
            syncButtonTestId="sync-specifications-btn"
          >
            <OverwriteExistingCheckbox
              checked={overwriteExisting}
              onChange={setOverwriteExisting}
            />
          </SyncOptionsStep>
        )}

        {syncState === "syncing" && (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-8">
            <div className="tw-animate-spin tw-rounded-full tw-h-10 tw-w-10 tw-border-b-2 tw-border-blue-600 tw-mb-4"></div>
            <p className="tw-text-gray-600 tw-font-medium">
              Syncing from InRiver...
            </p>
            <p className="tw-text-gray-500 tw-text-sm tw-mt-1">
              This may take a minute
            </p>
          </div>
        )}

        {syncState === "success" && result && (
          <div className="tw-text-center tw-py-4">
            <div className="tw-text-green-600 tw-mb-4">
              <i className="material-icons tw-text-5xl">check_circle</i>
            </div>
            <h6 className="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">
              Sync Complete
            </h6>
            <p className="tw-text-gray-600" data-testid="sync-success-msg">
              <span className="tw-font-semibold">{result.created_count}</span>{" "}
              {result.object_name} created,{" "}
              <span className="tw-font-semibold">{result.updated_count}</span>{" "}
              updated
            </p>
          </div>
        )}

        {syncState === "error" && result && (
          <div className="tw-text-center tw-py-4">
            <div className="tw-text-red-600 tw-mb-4">
              <i className="material-icons tw-text-5xl">error</i>
            </div>
            <h6 className="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">
              Sync Failed
            </h6>
            <p className="tw-text-gray-600" data-testid="sync-error-msg">
              {result.message}
            </p>
          </div>
        )}
      </Modal.Body>
      <Modal.Actions>
        {(syncState === "success" || syncState === "error") && (
          <Button
            variant="secondary"
            onClick={handleSyncAgain}
            data-testid="sync-again-btn"
          >
            Sync something else
          </Button>
        )}
        <Button
          variant={syncState === "idle" ? "secondary" : "primary"}
          onClick={handleClose}
        >
          Close
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
